import { selectAvailableCapabilityEntries, isCapabilityEntryAvailable } from '../../projection/capabilities/availability.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { selectPromptTemplateCards } from '../../projection/prompt-template/promptTemplateCardsSelector.js';
import {
  isAuthoritativeProjection,
  resolveRecommendationPanelProjection,
  selectLatestComposerUiProjection,
} from '../../projection/composer/composerUiProjectionSelector.js';
import { selectIntakeComposerChooser } from '../../projection/intake/intakeComposerChooserSelector.js';
import {
  canExpandScenarioCards,
  normalizeStarterScenarios,
  resolvePanelDismissed,
  resolveStarterSuppressed,
  resolveRecommendationTitle,
  selectVisibleScenarios,
} from '../../projection/recommendations/cards.js';
import { resolveComposerActionIntent } from '../../application/actions/dispatch.js';

const trimText = (value = '') => String(value || '').trim();
const MESSAGE_ACTION_HANDLERS = new Map([
  ['completed_message', (entry, onAction) => onAction?.(entry)],
]);
const DEFAULT_OPTIONS = Object.freeze({
  starterScenarios: [],
  timelineItems: [],
  composerLoading: false,
  composerError: null,
  defaultShowAllScenarios: false,
  showStarterScenarios: true,
  resolvedUILabels: {},
});

function resolveDismissKey(activeSessionId, availability) {
  return trimText(availability?.scopeKey || activeSessionId || '__default__') || '__default__';
}

function selectAvailablePromptCards({ composerLoading, promptCardSuppressed, timelineItems, availability }) {
  if (promptCardSuppressed) {
    return [];
  }
  return selectPromptTemplateCards({
    timelineItems, availability,
    currentTurnRunning: composerLoading === true,
  });
}

function resolvePromptPanelSuppressed({ composerLoading, hasPromptTemplateCards, promptCardSuppressed }) {
  return [promptCardSuppressed, composerLoading === true && !hasPromptTemplateCards].some(Boolean);
}

function selectRecommendationPanel(timelineItems) {
  return resolveRecommendationPanelProjection(
    selectLatestComposerUiProjection({ timelineItems }),
  );
}

function isPromptCardSuppressed({ recommendationPanel }) {
  const backendHidden = isAuthoritativeProjection(recommendationPanel)
    && recommendationPanel.visible === false;
  return backendHidden;
}

function useRecommendationConsumption({ dismissKey }) {
  const [consumed, setConsumed] = useState(() => new Set());
  const setRecommendationConsumed = useCallback(() => {
    setConsumed(previous => new Set([...previous, dismissKey]));
  }, [dismissKey]);
  return [consumed.has(dismissKey), setRecommendationConsumed];
}

export function useScenarioPromptCards(options = {}) {
  const {
    activeSessionId,
    workspaceSurface,
    workspaceSuspension,
    availability,
    modulePromptRegistry,
    starterScenarios,
    timelineItems,
    composerLoading,
    defaultShowAllScenarios,
    showStarterScenarios,
    resolvedUILabels,
    composerOnChange,
    onMessageCapabilityAction,
  } = { ...DEFAULT_OPTIONS, ...options };
  const dismissKey = resolveDismissKey(activeSessionId, availability);
  const activeScope = useRef({ dismissKey, visibleScenarios: [], active: true });
  useEffect(() => {
    activeScope.current.active = true;
    return () => { activeScope.current.active = false; };
  }, []);
  const [showAllScenarios, setShowAllScenarios] = useState(defaultShowAllScenarios === true);
  const [dismissState, setDismissState] = useState({});
  const recommendationPanel = useMemo(() => selectRecommendationPanel(timelineItems), [timelineItems]);
  const activeComposerChooser = useMemo(
    () => selectIntakeComposerChooser({ timelineItems }),
    [timelineItems],
  );
  const promptCardSuppressed = isPromptCardSuppressed({
    activeComposerChooser,
    recommendationPanel,
  });
  const promptTemplateCards = useMemo(() => selectAvailablePromptCards({
    composerLoading,
    promptCardSuppressed,
    timelineItems, availability,
  }), [availability, composerLoading, promptCardSuppressed, timelineItems]);
  const hasPromptTemplateCards = promptTemplateCards.length > 0;
  const [recommendationConsumed, setRecommendationConsumed] = useRecommendationConsumption({
    dismissKey,
  });
  const normalizedStarterScenarios = useMemo(
    () => selectAvailableCapabilityEntries(normalizeStarterScenarios(starterScenarios), availability, modulePromptRegistry),
    [availability, modulePromptRegistry, starterScenarios],
  );
  const starterScenariosSuppressed = resolveStarterSuppressed({
    workspaceSurface,
    workspaceSuspension,
    activeComposerChooser,
    composerLoading,
    showStarterScenarios,
    recommendationPanel,
  });
  const promptPanelSuppressed = resolvePromptPanelSuppressed({
    composerLoading,
    hasPromptTemplateCards,
    promptCardSuppressed,
  });
  const randomSeed = useMemo(
    () => ({ dismissKey, value: Math.random() }),
    [dismissKey],
  ).value;

  useEffect(() => {
    setShowAllScenarios(defaultShowAllScenarios === true);
  }, [defaultShowAllScenarios, dismissKey]);

  const selection = {
    hasPromptTemplateCards,
    normalizedStarterScenarios,
    promptPanelSuppressed,
    promptTemplateCards,
    scenarioRandomSeed: randomSeed,
    showAllScenarios,
    starterScenariosSuppressed,
  };
  const visibleScenarios = selectVisibleScenarios(selection);
  // A callback retained by a previous render must not prefill a now-ineligible starter.
  activeScope.current = { dismissKey, visibleScenarios, active: true };
  const canExpandScenarios = canExpandScenarioCards(selection);
  const recommendationPanelDismissed = resolvePanelDismissed({
    hasPromptTemplateCards,
    promptPanelSuppressed,
    scenarioDismissed: dismissState[dismissKey] === true,
    scenarioRecommendationConsumed: recommendationConsumed,
    starterScenariosSuppressed,
  });
  const recommendationPanelTitle = resolveRecommendationTitle({
    recommendationPanel, starterScenariosSuppressed, resolvedUILabels,
  });

  const handleScenarioCardClick = useCallback((entry) => {
    if (!activeScope.current.active || activeScope.current.dismissKey !== dismissKey) return;
    // Message cards retain their identity checks and only update Core's composer draft.
    const messageHandler = MESSAGE_ACTION_HANDLERS.get(entry?.messageActionSurface);
    if (messageHandler) return messageHandler(entry, onMessageCapabilityAction);
    const prepared = activeScope.current.visibleScenarios.find(card => card.key === entry?.key && card.prompt === entry?.prompt);
    const readCurrent = availability?.readCurrent ?? (() => availability);
    const current = readCurrent();
    if (!prepared || !isCapabilityEntryAvailable(prepared, current, modulePromptRegistry)) return;
    const intent = resolveComposerActionIntent(prepared);
    if (intent.kind !== 'prefill') {
      return;
    }
    if (trimText(prepared.source) !== 'prompt_template_action') {
      setDismissState((previous) => (
        previous[dismissKey] ? previous : { ...previous, [dismissKey]: true }
      ));
      setRecommendationConsumed(true);
    }
    setShowAllScenarios(false);
    composerOnChange?.({ target: { value: intent.content } });
  }, [availability, composerOnChange, dismissKey, modulePromptRegistry, onMessageCapabilityAction, setRecommendationConsumed]);

  return {
    canExpandScenarios,
    recommendationPanelDismissed,
    recommendationPanelTitle,
    showAllScenarios,
    setShowAllScenarios,
    visibleScenarios,
    handleScenarioCardClick,
  };
}

export default useScenarioPromptCards;
